import React, { useState, useEffect } from 'react';
import FollowUpService from '../services/followUpService';
import { LeadsService } from '../services/leadsService';

const FollowUpManager = ({ leads: propLeads, dashboardFollowUps, onUpdateLead }) => {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [allLeads, setAllLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState('');
  const [followUpType, setFollowUpType] = useState('call');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('');
  const [summary, setSummary] = useState('');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState('medium');
  const [leadSearch, setLeadSearch] = useState('');
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [visibleHistoryIds, setVisibleHistoryIds] = useState(new Set());
  const [rescheduleModal, setRescheduleModal] = useState({ isOpen: false, followUp: null });
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  useEffect(() => {
    loadFollowUps();
    loadAllLeads();
  }, []);

  const loadAllLeads = async () => {
    try {
      if (propLeads && propLeads.length > 0) {
        setAllLeads(propLeads);
      } else {
        const result = await LeadsService.getAllLeads();
        if (result.success && result.data) setAllLeads(result.data);
      }
    } catch (e) {
      console.error('Error loading leads:', e);
    }
  };

  const loadFollowUps = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const upcomingResult = await FollowUpService.getUpcomingFollowUps();
      const overdueResult = await FollowUpService.getOverdueFollowUps();
      let all = [];
      if (dashboardFollowUps && dashboardFollowUps.length > 0) all = [...dashboardFollowUps];
      if (upcomingResult.success && upcomingResult.data) all = [...all, ...upcomingResult.data];
      if (overdueResult.success && overdueResult.data) all = [...all, ...overdueResult.data];

      const normalized = all.map(f => {
        const id = f._id || f.id || f.followUpId;
        if (f.followUp && typeof f.followUp === 'object') {
          return {
            ...f.followUp,
            _id: f.followUp._id || id,
            id: f.followUp._id || id,
            leadId: f.leadId || f._id,
            leadName: f.leadName || f.name,
            leadCompany: typeof f.leadCompany === 'string' ? f.leadCompany : (f.leadCompany?.name || f.company || ''),
            scheduledDate: f.followUp.nextFollowUpDate || f.followUp.scheduledDate || f.followUp.date,
            rescheduled: f.followUp.rescheduled || false,
            rescheduleHistory: f.followUp.rescheduleHistory || []
          };
        }
        return {
          ...f,
          _id: id,
          id: id,
          leadCompany: typeof f.leadCompany === 'string' ? f.leadCompany : (f.leadCompany?.name || f.company || ''),
          scheduledDate: f.nextFollowUpDate || f.scheduledDate || f.date,
          rescheduled: f.rescheduled || false,
          rescheduleHistory: f.rescheduleHistory || []
        };
      });

      const unique = normalized.reduce((acc, cur) => {
        const cid = cur._id || cur.id;
        if (!cid || acc.find(x => (x._id || x.id) === cid)) return acc;
        acc.push(cur);
        return acc;
      }, []);

      // Also pull from leads
      try {
        const leadsResult = await LeadsService.getAllLeads();
        if (leadsResult.success && leadsResult.data) {
          leadsResult.data.forEach(lead => {
            if (lead.followUps && lead.followUps.length > 0) {
              lead.followUps.forEach(fu => {
                const fid = fu._id || fu.id;
                if (!fid || unique.find(x => (x._id || x.id) === fid)) return;
                unique.push({
                  ...fu,
                  _id: fid,
                  id: fid,
                  leadId: lead._id || lead.id,
                  leadName: lead.name,
                  leadCompany: typeof lead.company === 'string' ? lead.company : (lead.company?.name || ''),
                  scheduledDate: fu.nextFollowUpDate || fu.scheduledDate || fu.date,
                  rescheduled: fu.rescheduled || false,
                  rescheduleHistory: fu.rescheduleHistory || []
                });
              });
            }
          });
        }
      } catch (e) {}

      setFollowUps(unique);
    } catch (e) {
      console.error('Error loading follow-ups:', e);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const addFollowUp = async () => {
    if (!selectedLead || !followUpDate || !followUpTime) {
      alert('Please fill all required fields');
      return;
    }
    const lead = allLeads.find(l => (l._id || l.id) === selectedLead);
    if (!lead) { alert('Lead not found'); return; }
    
    let currentUser = null;
    try {
      const storedUser = localStorage.getItem('userInfo') || localStorage.getItem('userData');
      if (storedUser) currentUser = JSON.parse(storedUser);
    } catch (e) {
      console.error('Error parsing user info:', e);
    }

    const localDate = new Date(`${followUpDate}T${followUpTime}`);
    const combinedDate = !isNaN(localDate.getTime()) ? localDate.toISOString() : `${followUpDate}T${followUpTime}`;

    const result = await FollowUpService.addFollowUp(lead._id || lead.id, {
      type: followUpType,
      summary: summary.trim() || notes.trim() || `${followUpType.charAt(0).toUpperCase() + followUpType.slice(1)} Follow-up`,
      notes,
      nextFollowUpDate: combinedDate,
      scheduledDate: combinedDate,
      priority,
      userId: currentUser?.id || currentUser?._id
    });
    if (result.success) {
      await loadFollowUps();
      setShowAddForm(false);
      setSelectedLead('');
      setFollowUpDate('');
      setFollowUpTime('');
      setSummary('');
      setNotes('');
    } else {
      alert(result.message || 'Failed to add follow-up');
    }
  };

  const completeFollowUp = async (fu) => {
    const lid = fu.leadId;
    const fid = fu._id || fu.id;
    if (!lid || !fid) return;
    await FollowUpService.updateFollowUp(lid, fid, { status: 'completed' });
    await loadFollowUps(true);
  };

  const deleteFollowUp = async (fu) => {
    if (!window.confirm('Delete this follow-up?')) return;
    const lid = fu.leadId;
    const fid = fu._id || fu.id;
    if (!lid || !fid) return;
    await FollowUpService.deleteFollowUp(lid, fid);
    await loadFollowUps(true);
  };

  const openRescheduleModal = (followUp) => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const localNow = new Date(now.getTime() - (offset * 60 * 1000));
    setRescheduleDate(localNow.toISOString().slice(0, 16));
    setRescheduleReason('');
    setRescheduleModal({ isOpen: true, followUp });
  };

  const handleReschedule = async () => {
    if (!rescheduleDate || !rescheduleReason.trim()) {
      alert('Please fill all required fields');
      return;
    }
    setRescheduleLoading(true);
    try {
      const fu = rescheduleModal.followUp;
      const lid = fu.leadId;
      const fid = fu._id || fu.id;
      if (!lid || !fid) return;

      let currentUser = null;
      try {
        const storedUser = localStorage.getItem('userInfo') || localStorage.getItem('userData');
        if (storedUser) currentUser = JSON.parse(storedUser);
      } catch (e) {
        console.error('Error parsing user info:', e);
      }

      const userName = currentUser?.name || currentUser?.teamMemberName || currentUser?.firstName || currentUser?.fullName || currentUser?.displayName || 'Unknown';
      const userEmail = currentUser?.email || currentUser?.businessEmail || currentUser?.teamMemberEmail || '';

      const previousDate = fu.nextFollowUpDate || fu.scheduledDate || fu.date;
      
      const newHistoryEntry = {
        previousDate: previousDate,
        newDate: new Date(rescheduleDate).toISOString(),
        reason: rescheduleReason,
        notes: '',
        rescheduledAt: new Date().toISOString(),
        rescheduledBy: userName,
        rescheduledByEmail: userEmail
      };

      const existingHistory = fu.rescheduleHistory || [];
      const updatedHistory = [...existingHistory, newHistoryEntry];

      await FollowUpService.updateFollowUp(lid, fid, {
        scheduledDate: new Date(rescheduleDate).toISOString(),
        nextFollowUpDate: new Date(rescheduleDate).toISOString(),
        rescheduled: true,
        rescheduledAt: new Date().toISOString(),
        rescheduledFrom: previousDate,
        rescheduleHistory: updatedHistory
      });

      setRescheduleModal({ isOpen: false, followUp: null });
      alert('Follow-up rescheduled successfully');
      await loadFollowUps(true);
    } catch (err) {
      console.error('Error rescheduling:', err);
      alert('Failed to reschedule follow-up');
    } finally {
      setRescheduleLoading(false);
    }
  };

  const toggleHistory = (id) => {
    setVisibleHistoryIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const isOverdue = (date) => date && new Date(date) < new Date() && new Date(date).toDateString() !== new Date().toDateString();

  const now = new Date();
  const filtered = followUps.filter(f => {
    if (filter === 'pending') return f.status !== 'completed' && !f.rescheduled;
    if (filter === 'completed') return f.status === 'completed';
    if (filter === 'overdue') return f.status !== 'completed' && isOverdue(f.scheduledDate);
    if (filter === 'rescheduled') return f.rescheduled === true && f.status !== 'completed';
    if (filter === 'today') return f.scheduledDate && new Date(f.scheduledDate).toDateString() === now.toDateString();
    return true;
  }).sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));

  const pendingCount = followUps.filter(f => f.status !== 'completed' && !f.rescheduled).length;
  const overdueCount = followUps.filter(f => f.status !== 'completed' && isOverdue(f.scheduledDate)).length;
  const rescheduledCount = followUps.filter(f => f.rescheduled && f.status !== 'completed').length;
  const todayCount = followUps.filter(f => f.scheduledDate && new Date(f.scheduledDate).toDateString() === now.toDateString()).length;

  const typeIcon = t => ({ call: '📞', email: '📧', meeting: '🤝', demo: '💻', proposal: '📋' }[t] || '📝');
  const priorityColor = p => ({ high: 'bg-red-100 text-red-800', medium: 'bg-yellow-100 text-yellow-800', low: 'bg-green-100 text-green-800' }[p] || 'bg-gray-100 text-gray-800');

  const fmtDate = d => d && !isNaN(new Date(d)) ? new Date(d).toLocaleDateString('en-IN') : 'No Date';
  const fmtTime = d => d && !isNaN(new Date(d)) ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';

  const filteredLeads = allLeads.filter(l => {
    const q = leadSearch.toLowerCase();
    return !q || (l.name || '').toLowerCase().includes(q) || (l.email || '').toLowerCase().includes(q) || (l.phone || '').includes(q);
  });

  const selectedLeadInfo = allLeads.find(l => (l._id || l.id) === selectedLead);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-1">📅 Follow-Up Manager</h2>
            <p className="text-gray-600">Schedule and track your lead follow-ups</p>
          </div>
          <button onClick={() => setShowAddForm(!showAddForm)} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg flex items-center gap-2 font-semibold">
            ➕ Schedule Follow-Up
          </button>
        </div>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
          {[
            { label: 'My Follow-ups', count: followUps.length, color: 'blue' },
            { label: 'Pending', count: pendingCount, color: 'orange' },
            { label: 'Rescheduled', count: rescheduledCount, color: 'purple' },
            { label: 'Overdue', count: overdueCount, color: 'red' },
            { label: 'Today', count: todayCount, color: 'green' }
          ].map(s => (
            <div key={s.label} className={`bg-${s.color}-50 rounded-xl p-4 text-center`}>
              <p className={`text-2xl font-bold text-${s.color}-600`}>{s.count}</p>
              <p className={`text-sm text-${s.color}-700`}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl">
          <h3 className="text-xl font-bold text-gray-800 mb-4">📝 Schedule New Follow-Up</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Lead Select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Lead *</label>
              <div className="relative">
                <button type="button" onClick={() => setShowLeadDropdown(!showLeadDropdown)} className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white text-left flex justify-between items-center hover:bg-gray-50">
                  <span>{selectedLeadInfo ? selectedLeadInfo.name : <span className="text-gray-400">Choose a lead...</span>}</span>
                  <span className="text-gray-400">{showLeadDropdown ? '▲' : '▼'}</span>
                </button>
                {showLeadDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-72 overflow-hidden">
                    <div className="p-3 border-b">
                      <input type="text" placeholder="Search..." value={leadSearch} onChange={e => setLeadSearch(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" autoFocus />
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      {filteredLeads.map(l => (
                        <div key={l._id || l.id} onClick={() => { setSelectedLead(l._id || l.id); setShowLeadDropdown(false); setLeadSearch(''); }} className={`px-4 py-3 cursor-pointer hover:bg-purple-50 border-b last:border-b-0 ${(l._id || l.id) === selectedLead ? 'bg-purple-100' : ''}`}>
                          <div className="font-medium">{l.name}</div>
                          <div className="text-xs text-gray-500">{l.company} • {l.email}</div>
                        </div>
                      ))}
                      {filteredLeads.length === 0 && <div className="p-4 text-center text-gray-500">No leads found</div>}
                    </div>
                    <div className="p-2 border-t bg-gray-50">
                      <button onClick={() => { setShowLeadDropdown(false); setLeadSearch(''); }} className="w-full text-sm text-gray-500 py-1">Close</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select value={followUpType} onChange={e => setFollowUpType(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl">
                <option value="call">📞 Phone Call</option>
                <option value="email">📧 Email</option>
                <option value="meeting">🤝 Meeting</option>
                <option value="demo">💻 Demo</option>
                <option value="proposal">📋 Proposal</option>
              </select>
            </div>
            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
              <input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="w-full px-4 py-3 border border-gray-200 rounded-xl" />
            </div>
            {/* Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time *</label>
              <input type="time" value={followUpTime} onChange={e => setFollowUpTime(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl" />
            </div>
            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl">
                <option value="low">🟢 Low</option>
                <option value="medium">🟡 Medium</option>
                <option value="high">🔴 High</option>
              </select>
            </div>
            {/* Summary */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Summary</label>
              <input type="text" value={summary} onChange={e => setSummary(e.target.value)} placeholder="Brief summary of follow-up..." className="w-full px-4 py-3 border border-gray-200 rounded-xl" />
            </div>
            {/* Notes */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add notes..." rows="3" className="w-full px-4 py-3 border border-gray-200 rounded-xl resize-none" />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={addFollowUp} className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700">✅ Schedule</button>
            <button onClick={() => setShowAddForm(false)} className="bg-gray-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-600">❌ Cancel</button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-white/20 shadow-xl">
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: '📊 All My Follow-ups', count: followUps.length },
            { key: 'pending', label: '⏳ Pending', count: pendingCount },
            { key: 'rescheduled', label: '🔄 Rescheduled', count: rescheduledCount },
            { key: 'today', label: '📅 Today', count: todayCount },
            { key: 'overdue', label: '🚨 Overdue', count: overdueCount },
            { key: 'completed', label: '✅ Completed', count: followUps.filter(f => f.status === 'completed').length }
          ].map(opt => (
            <button key={opt.key} onClick={() => setFilter(opt.key)} className={`px-4 py-2 rounded-xl font-medium transition-all ${filter === opt.key ? 'bg-purple-600 text-white shadow-lg' : 'bg-white/70 text-gray-700 hover:bg-purple-100'}`}>
              {opt.label} ({opt.count})
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">
            <div className="animate-spin inline-block w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mb-4"></div>
            <p>Loading follow-ups...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Lead Info</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Schedule</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Priority</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="text-4xl mb-3">📋</div>
                      <h3 className="text-lg font-bold text-gray-700">No Follow-ups Found</h3>
                      <p className="text-gray-500 text-sm">{filter === 'all' ? 'Schedule your first follow-up!' : `No ${filter} follow-ups.`}</p>
                    </td>
                  </tr>
                ) : filtered.map((fu, idx) => {
                  const fid = fu._id || fu.id;
                  const isHistVis = visibleHistoryIds.has(fid);
                  return (
                    <React.Fragment key={`${fid}-${idx}`}>
                      <tr className={`hover:bg-purple-50/30 transition-colors ${isOverdue(fu.scheduledDate) && fu.status !== 'completed' ? 'bg-red-50/30' : ''}`}>
                        <td className="px-6 py-4">
                          <div className="font-bold text-gray-900">{fu.leadName || '—'}</div>
                          {fu.leadCompany && typeof fu.leadCompany === 'string' && !/^[0-9a-fA-F]{24}$/.test(fu.leadCompany) && (
                            <div className="text-xs text-gray-500">{fu.leadCompany}</div>
                          )}
                          {fu.notes && <div className="text-xs text-gray-400 mt-1 truncate max-w-xs">{fu.notes}</div>}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xl">{typeIcon(fu.type)}</span>
                          <span className="ml-2 text-sm text-gray-700 capitalize">{fu.type || '—'}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-semibold text-gray-800">📅 {fmtDate(fu.scheduledDate)}</div>
                          <div className="text-xs text-gray-500">🕐 {fmtTime(fu.scheduledDate)}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${priorityColor(fu.priority)}`}>
                            {(fu.priority || 'medium').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${
                            fu.status === 'completed' 
                              ? 'bg-green-100 text-green-800' 
                              : isOverdue(fu.scheduledDate)
                                ? 'bg-red-100 text-red-800'
                                : 'bg-orange-100 text-orange-800'
                          }`}>
                            {fu.status === 'completed' 
                              ? '✅ Completed' 
                              : isOverdue(fu.scheduledDate)
                                ? 'Overdue'
                                : '⏳ Pending'}
                          </span>
                          {fu.rescheduled && (
                            <button onClick={() => toggleHistory(fid)} className="block text-xs text-blue-600 underline mt-1 text-left">
                              🔄 Rescheduled ({fu.rescheduleHistory?.length || 0}x)
                            </button>
                          )}
                          {fu.rescheduled && fu.rescheduleHistory && fu.rescheduleHistory.length > 0 && (() => {
                            const latestHistory = fu.rescheduleHistory[fu.rescheduleHistory.length - 1];
                            if (!latestHistory || !latestHistory.reason) return null;
                            return (
                              <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-900 max-w-[180px] text-left">
                                <div className="font-semibold">Reason:</div>
                                <div className="break-words">{latestHistory.reason}</div>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2 flex-wrap">
                            {fu.status !== 'completed' && (
                              <button onClick={() => completeFollowUp(fu)} className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-semibold hover:bg-green-200">
                                ✅ Complete
                              </button>
                            )}
                            <button onClick={() => deleteFollowUp(fu)} className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-200">
                              🗑️ Delete
                            </button>
                            <button onClick={() => openRescheduleModal(fu)} className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-200">
                              🔄 Reschedule
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isHistVis && fu.rescheduleHistory && fu.rescheduleHistory.length > 0 && (
                        <tr>
                          <td colSpan="6" className="px-6 py-3 bg-blue-50">
                            <div className="text-xs font-bold text-blue-700 mb-2">Reschedule History:</div>
                            {fu.rescheduleHistory.map((h, i) => (
                              <div key={i} className="text-xs text-gray-600 mb-1">
                                • {fmtDate(h.previousDate)} → {fmtDate(h.newDate)} {h.reason && `(${h.reason})`}
                              </div>
                            ))}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reschedule Modal */}
      {rescheduleModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between bg-white">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                🔄 Reschedule Follow-up
              </h3>
              <button 
                onClick={() => setRescheduleModal({ isOpen: false, followUp: null })} 
                className="text-gray-400 hover:text-gray-600 text-2xl p-1 leading-none"
              >
                &times;
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Rescheduling follow-up for <span className="font-bold text-gray-800">{rescheduleModal.followUp?.leadName || '—'}</span>.
              </p>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  New Date and Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Reason for Rescheduling <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                  placeholder="Please enter the reason for rescheduling..."
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3 rounded-b-2xl">
              <button
                onClick={() => setRescheduleModal({ isOpen: false, followUp: null })}
                className="px-5 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleReschedule}
                disabled={rescheduleLoading || !rescheduleDate || !rescheduleReason.trim()}
                className="px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {rescheduleLoading && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
                {rescheduleLoading ? 'Saving...' : 'Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowUpManager;
